import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  fetchAllPeople,
  isStatsEligible,
} from "../peopleStats/peopleStatsData";
import { useNavigate } from "react-router-dom";
import menuIcon from "../../assets/images/menuIcon.svg";
import notificationIcon from "../../assets/images/notification.svg";
import searchIcon from "../../assets/images/search.svg";
import closeIcon from "../../assets/images/closeIcon.svg";
import userAddIcon from "../../assets/images/userAddIcon.svg";
import InitialsAvatar from "../plugInElements/InitialsAvatar";
import {
  fetchLoggedUserInfo,
  fetchTimeline,
  fetchUserList,
  logoutMmt,
} from "./mmtApi";
import {
  buildDashboardUrl,
  buildPatientPageUrl,
  clearStoredSession,
  formatPersonName,
  getStoredSession,
  openAddTestPerson,
  openImportPeople,
  openEditTestPerson,
  openNotificationDestination,
} from "./mmtFlow";
import {
  getContactUsUrl,
  getPrivacyPolicyUrl,
  getTermsUrl,
} from "../../configs/environment";
import NotificationTimelineDrawer from "./NotificationTimelineDrawer";
import { getReviewState } from "./reviewState";
import MmtAddTestPerson from "../testPerson/MmtAddTestPerson";
import MmtEditTestPerson from "../testPerson/MmtEditTestPerson";
import MmtImportFlow from "../import/MmtImportFlow";
import { resolveCustomerType } from "../../utlis/personTerminology";
import { getActiveLocale } from "../../configs/regionalProfile";
import { useT } from "../../i18n/useT";
import { isWebView, openExternalUrl } from "../../utlis/webViewBridge";
import ExternalWebView from "../common/ExternalWebView";
import { genderLabel } from "../../utlis/genderLabel";
import "./mmtuserlist.scss";

const PAGE_LIMIT = 15;
const PENDING_PUSHES_KEY = "mmtPendingNativePushes";
const PERSON_TYPES = new Set([
  "senior_living",
  "home_care",
  "rehab",
  "primary_care",
  "pharmacy",
  "cognitive",
]);

const getPersonType = (account) => {
  const type = resolveCustomerType(account);
  return PERSON_TYPES.has(type) ? type : "test_person";
};

const getPendingPushes = () => {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(PENDING_PUSHES_KEY) || "[]",
    );
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

export default function MmtUserList() {
  const navigate = useNavigate();
  const t = useT("mmtUserList");
  const p = useT("population");
  const [total, setTotal] = useState(null);
  const [rosterRevision, setRosterRevision] = useState(0);
  const totalRequest = useRef(0);
  const listRequest = useRef(0);
  useEffect(
    () => () => {
      ++listRequest.current;
      ++totalRequest.current;
    },
    [],
  );
  /**
   * Duplicate External ID preflight for Import.
   *
   * `rowReasons` is synchronous, so it is given a lookup into IDs this account
   * has already loaded, warmed from the existing list endpoint. It is a
   * convenience check only: `POST /seniors` rejection remains authoritative and
   * is surfaced per row at import time.
   */
  const takenIdsRef = useRef(new Set());
  const importDuplicateCheck = useCallback(
    (externalId) =>
      takenIdsRef.current.has(
        String(externalId || "")
          .trim()
          .toLowerCase(),
      ),
    [],
  );
  // Stage 3 strings live in their own namespace; no English is hard-coded here.
  const [session] = useState(getStoredSession);
  const [userInfo, setUserInfo] = useState(null);
  const [users, setUsers] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState("createdAt_desc");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [pushUnreadCount, setPushUnreadCount] = useState(
    () => getPendingPushes().length,
  );
  const [hasCriticalPush, setHasCriticalPush] = useState(() =>
    getPendingPushes().some((item) => item.category === "CRITICAL"),
  );
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [externalPage, setExternalPage] = useState(null);
  // modal: null | { type: 'add' } | { type: 'edit', seniorUuid } | { type: 'view', seniorUuid }
  const [modal, setModal] = useState(null);
  // Floating action button: closed by default, expands to Add / Import options.
  const [fabOpen, setFabOpen] = useState(false);

  useEffect(() => {
    if (!session || rosterRevision === 0) return;
    let cancelled = false;
    const request = ++totalRequest.current;
    setTotal(null);
    fetchAllPeople({ session, fetchUserList, cancelled: () => cancelled })
      .then((people) => {
        if (!cancelled && request === totalRequest.current)
          setTotal(people.length);
      })
      .catch(() => {
        if (!cancelled && request === totalRequest.current) setTotal(null);
      });
    return () => {
      cancelled = true;
    };
  }, [session, rosterRevision]);

  useEffect(() => {
    if (!session) navigate("/login", { replace: true });
  }, [navigate, session]);

  const [sortField, sortOrder] = useMemo(
    () => sortOption.split("_"),
    [sortOption],
  );

  const loadUsers = useCallback(
    async (nextPage = 1, append = false) => {
      if (!session) return;
      const request = ++listRequest.current;
      append ? setLoadingMore(true) : setLoading(true);
      setError("");
      try {
        const data = await fetchUserList({
          session,
          page: nextPage,
          limit: PAGE_LIMIT,
          filter: search.trim(),
          sortField,
          sortOrder,
        });
        if (request !== listRequest.current) return;
        if (nextPage === 1 && !search.trim()) {
          const totalId = ++totalRequest.current;
          setTotal(null);
          fetchAllPeople({
            session,
            fetchUserList: (args) =>
              args.page === 1
                ? Promise.resolve(data)
                : fetchUserList({ ...args, sortField, sortOrder }),
          })
            .then((people) => {
              if (totalId === totalRequest.current) setTotal(people.length);
            })
            .catch(() => {
              if (totalId === totalRequest.current) setTotal(null);
            });
        }
        setUsers((current) => {
          const next = append ? [...current, ...data] : data;
          // Warm the Import duplicate preflight from IDs this account can see.
          takenIdsRef.current = new Set(
            next
              .map((u) =>
                String(
                  u?.user_id_from_client ??
                    u?.test_person_id ??
                    u?.testPersonId ??
                    "",
                )
                  .trim()
                  .toLowerCase(),
              )
              .filter(Boolean),
          );
          return next;
        });
        setHasMore(data.length === PAGE_LIMIT);
        setPage(nextPage);
      } catch (err) {
        if (request !== listRequest.current) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            t("unable_to_load", {
              people: t(`people.${getPersonType(userInfo)}.plural_lower`),
            }),
        );
      } finally {
        if (request === listRequest.current)
          append ? setLoadingMore(false) : setLoading(false);
      }
    },
    [search, session, sortField, sortOrder, t, userInfo],
  );

  useEffect(() => {
    if (!session) return;
    fetchLoggedUserInfo(session)
      .then((details) => {
        setUserInfo(details);
        sessionStorage.setItem("staffDetails", JSON.stringify(details));
      })
      .catch(() => {});
  }, [session]);

  useEffect(() => {
    const handleNativePush = (event) => {
      const notification = event.detail;
      if (!notification) return;
      setPushUnreadCount((count) => count + 1);
      if (notification.category === "CRITICAL") setHasCriticalPush(true);
    };

    window.addEventListener("native-notification-received", handleNativePush);
    return () =>
      window.removeEventListener(
        "native-notification-received",
        handleNativePush,
      );
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => loadUsers(1, false), 350);
    return () => window.clearTimeout(id);
  }, [loadUsers]);

  const openTimeline = async () => {
    if (!session) return;
    setTimelineOpen(true);
    setPushUnreadCount(0);
    setHasCriticalPush(false);
    sessionStorage.removeItem(PENDING_PUSHES_KEY);
    setTimelineLoading(true);
    try {
      setTimeline(await fetchTimeline(session));
    } catch {
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const goTo = (url) => {
    if (url.startsWith("/")) navigate(url);
    else window.location.href = url;
  };

  const handleNotificationPress = (notification) => {
    setTimelineOpen(false);
    openNotificationDestination(notification, session, navigate);
  };

  const openSidebarUrl = (url, title) => {
    setMenuOpen(false);
    if (isWebView()) {
      setExternalPage({ url, title });
      return;
    }
    openExternalUrl(url);
  };

  const handleLogout = async () => {
    if (session) await logoutMmt(session);
    clearStoredSession();
    navigate("/login", { replace: true });
  };

  if (!session) return null;

  const personType = getPersonType(userInfo);
  const isPharmacy = personType === "pharmacy";
  // Pharmacy opens the patient straight into the plan view (DAP87); other
  // customer types keep their existing dashboard landing tab.
  const openPatient = (seniorId) =>
    goTo(
      isPharmacy
        ? buildPatientPageUrl(session, seniorId)
        : buildDashboardUrl(session, seniorId),
    );
  const personLabels = {
    singular: t(`people.${personType}.singular`),
    plural: t(`people.${personType}.plural`),
    singularLower: t(`people.${personType}.singular_lower`),
    pluralLower: t(`people.${personType}.plural_lower`),
  };

  return (
    <main className="mmt-user-list-page">
      <header className="mmt-user-header">
        <button
          className="mmt-header-icon"
          onClick={() => setMenuOpen(true)}
          aria-label={t("open_menu")}
        >
          <img src={menuIcon} alt="" />
        </button>
        <div className="mmt-user-welcome">
          <InitialsAvatar
            name={userInfo?.name}
            size={40}
            className="mmt-staff-initials"
          />
          <strong>{t("welcome", { name: userInfo?.name || "" })}</strong>
        </div>
        <button
          className={`mmt-header-icon mmt-notification-button${hasCriticalPush ? " is-critical" : ""}`}
          onClick={openTimeline}
          aria-label={t("open_notifications")}
        >
          <img src={notificationIcon} alt="" />
          {pushUnreadCount > 0 ? (
            <span className="mmt-notification-count">
              {pushUnreadCount > 99 ? "99+" : pushUnreadCount}
            </span>
          ) : null}
        </button>
      </header>

      <section className="mmt-list-toolbar">
        <h1>
          {personLabels.plural}
          {total !== null
            ? ` (${new Intl.NumberFormat(getActiveLocale()).format(total)})`
            : ""}
        </h1>
        <div className="mmt-search-sort">
          <label className="mmt-search">
            <img src={searchIcon} alt="" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("search_placeholder")}
            />
            {search ? (
              <button
                onClick={() => setSearch("")}
                aria-label={t("clear_search")}
              >
                <img src={closeIcon} alt="" />
              </button>
            ) : null}
          </label>
          <select
            value={sortOption}
            className={
              sortOption.startsWith("pharmacyDapStatus_")
                ? "is-status-sort"
                : undefined
            }
            onChange={(event) => setSortOption(event.target.value)}
          >
            <option value="createdAt_desc">{t("sort_newest")}</option>
            <option value="createdAt_asc">{t("sort_oldest")}</option>
            {isPharmacy ? (
              <>
                <option value="pharmacyDapStatus_needsReviewFirst">
                  {t("sort_needs_review")}
                </option>
                <option value="pharmacyDapStatus_checkReportFirst">
                  {t("sort_check_report")}
                </option>
                <option value="pharmacyDapStatus_reviewedFirst">
                  {t("sort_reviewed")}
                </option>
              </>
            ) : null}
          </select>
        </div>
      </section>

      <section className="mmt-users">
        {(() => {
          if (loading) {
            return <div className="mmt-state">{t("loading")}</div>;
          }
          if (error) {
            return (
              <div className="mmt-state">
                <p>{error}</p>
                <button onClick={() => loadUsers(1, false)}>
                  {t("retry")}
                </button>
              </div>
            );
          }
          if (users.length === 0) {
            return (
              <div className="mmt-state">
                {search
                  ? t("no_matching", { people: personLabels.pluralLower })
                  : t("none_found", { people: personLabels.pluralLower })}
              </div>
            );
          }

          return (
            <>
              {users.map((senior) => {
                // Name order is regional, not linguistic; the resolver owns it.
                const name = formatPersonName(
                  senior.first_name,
                  senior.last_name,
                );
                const { needsReview, status } = getReviewState(senior);
                // DAP87 status pill. Solid, white text; sits in the right
                // column above the edit pencil. Opens Mobility Metrics.
                const pill = (
                  <span
                    className={`mmt-review-action is-${status.replace(
                      "_",
                      "-",
                    )}`}
                    role="button"
                    tabIndex={0}
                    onClick={(event) => {
                      event.stopPropagation();
                      openPatient(senior.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.stopPropagation();
                        event.preventDefault();
                        openPatient(senior.id);
                      }
                    }}
                  >
                    {status === "check_report" && (
                      <svg
                        viewBox="0 0 16 16"
                        width="16"
                        height="16"
                        aria-hidden="true"
                      >
                        <path
                          d="M13.5 4.5 6.5 11.5 2.5 7.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    )}
                    {status === "reviewed"
                      ? t("reviewed")
                      : needsReview
                        ? t("needs_review")
                        : t("check_report")}
                  </span>
                );
                return (
                  <article
                    role="button"
                    tabIndex={0}
                    aria-label={name || senior.name}
                    onKeyDown={(event) => {
                      if (
                        event.target === event.currentTarget &&
                        (event.key === "Enter" || event.key === " ")
                      ) {
                        event.preventDefault();
                        openPatient(senior.id);
                      }
                    }}
                    className="mmt-user-card"
                    key={senior.id}
                    onClick={() => openPatient(senior.id)}
                  >
                    <InitialsAvatar
                      className="mmt-user-avatar"
                      name={name || senior.name}
                      size={60}
                    />
                    <span className="mmt-user-card-copy">
                      <strong className="notranslate">
                        {name || senior.name}
                      </strong>
                      <span className="mmt-badges">
                        <span>
                          {t("id_label")}
                          {"\u00a0"}
                          <span className="notranslate">
                            {senior.test_person_id}
                          </span>
                        </span>
                        {senior.gender || senior.age > 0 ? (
                          <span>
                            {genderLabel(senior.gender)}
                            {senior.gender && senior.age > 0 ? <i /> : null}
                            {senior.age > 0 ? senior.age : ""}
                          </span>
                        ) : null}
                      </span>
                      {senior.isIncompleteProfile ? (
                        <em
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditTestPerson(session, senior.id, setModal);
                          }}
                        >
                          {t("complete_profile")}
                        </em>
                      ) : null}
                    </span>
                    <span className="mmt-user-actions">
                      {pill}
                      {/* Edit this test person — opens the edit modal. */}
                      <span
                        className="mmt-user-edit"
                        role="button"
                        tabIndex={0}
                        aria-label={t("edit")}
                        title={t("edit")}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditTestPerson(session, senior.id, setModal);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.stopPropagation();
                            event.preventDefault();
                            openEditTestPerson(session, senior.id, setModal);
                          }
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </span>
                    </span>
                  </article>
                );
              })}
              {hasMore ? (
                <button
                  className="mmt-load-more"
                  onClick={() => loadUsers(page + 1, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? t("loading") : t("load_more")}
                </button>
              ) : null}
            </>
          );
        })()}
      </section>

      <button
        className="mmt-add-person"
        onClick={() => setFabOpen(true)}
        aria-haspopup="dialog"
      >
        <img src={userAddIcon} alt="" />
        {t("add_person", { person: personLabels.singular })}
      </button>
      <Dialog
        open={fabOpen}
        onClose={() => setFabOpen(false)}
        aria-labelledby="mmt-add-choice-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="mmt-add-choice-title">
          {t("add_person", { person: personLabels.singular })}
        </DialogTitle>
        <DialogContent className="mmt-add-choices">
          <Button
            onClick={() => {
              setFabOpen(false);
              openAddTestPerson(session, setModal);
            }}
          >
            {p("Add one manually")}
          </Button>
          <Button
            onClick={() => {
              setFabOpen(false);
              openImportPeople(session, setModal);
            }}
          >
            {p("Upload PDF or Excel")}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFabOpen(false)}>{p("Cancel")}</Button>
        </DialogActions>
      </Dialog>

      <div
        className={`mmt-drawer-overlay ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <button
          className="mmt-drawer-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-label={t("close_menu")}
        />
        <aside className="mmt-menu-drawer">
          <button
            className="mmt-menu-close"
            onClick={() => setMenuOpen(false)}
            aria-label={t("close_menu")}
          >
            <img src={closeIcon} alt="" />
          </button>

          <div className="mmt-menu-profile">
            <InitialsAvatar
              className="mmt-menu-avatar mmt-staff-initials"
              name={userInfo?.name}
              size={56}
            />
            <div className="mmt-menu-profile-info">
              <strong>{userInfo?.name || "Senior Life"}</strong>
              {userInfo?.email ? <span>{userInfo.email}</span> : null}
            </div>
          </div>

          <div className="mmt-menu-divider" />

          <nav className="mmt-menu-items">
            {isStatsEligible(personType) && (
              <MenuEntry
                icon="stats"
                title={p(
                  personType === "senior_living"
                    ? "Resident Stats"
                    : "Client Stats",
                )}
                description={p("stats_description")}
                featured
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/people-stats");
                }}
              />
            )}
            <MenuEntry
              icon="password"
              title={p("Change Password")}
              description={p("password_description")}
              onClick={() => {
                setMenuOpen(false);
                navigate("/change-password");
              }}
            />
            <MenuEntry
              icon="privacy"
              title={t("privacy_title")}
              description={p("privacy_description")}
              onClick={() =>
                openSidebarUrl(
                  getPrivacyPolicyUrl(getActiveLocale()),
                  t("privacy_title"),
                )
              }
            />
            <MenuEntry
              icon="terms"
              title={t("terms_title")}
              description={p("terms_description")}
              onClick={() =>
                openSidebarUrl(getTermsUrl(getActiveLocale()), t("terms_title"))
              }
            />
            <MenuEntry
              icon="support"
              title={t("support_title")}
              description={p("support_description")}
              onClick={() =>
                openSidebarUrl(
                  getContactUsUrl(getActiveLocale()),
                  t("support_title"),
                )
              }
            />
            <MenuEntry
              icon="logout"
              title={t("logout")}
              description={p("logout_description")}
              onClick={() => {
                setMenuOpen(false);
                setLogoutConfirm(true);
              }}
            />
          </nav>
        </aside>
      </div>

      <ExternalWebView
        page={externalPage}
        onClose={() => setExternalPage(null)}
      />

      {logoutConfirm ? (
        <div className="mmt-confirm-overlay">
          <div className="mmt-confirm-dialog">
            <strong>{t("logout")}</strong>
            <p>{t("logout_confirmation")}</p>
            <div className="mmt-confirm-actions">
              <button onClick={() => setLogoutConfirm(false)}>
                {t("cancel")}
              </button>
              <button
                className="mmt-confirm-destructive"
                onClick={() => {
                  setLogoutConfirm(false);
                  handleLogout();
                }}
              >
                {t("log_out")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <NotificationTimelineDrawer
        open={timelineOpen}
        timeline={timeline}
        loading={timelineLoading}
        onClose={() => setTimelineOpen(false)}
        onNotificationPress={handleNotificationPress}
      />

      {modal ? (
        <div className="mmt-tp-overlay">
          {modal.type === "add" ? (
            <MmtAddTestPerson
              onClose={() => setModal(null)}
              onSuccess={() => {
                setModal(null);
                loadUsers(1, false);
                setRosterRevision((value) => value + 1);
              }}
            />
          ) : modal.type === "edit" ? (
            <MmtEditTestPerson
              seniorUuidProp={modal.seniorUuid}
              onClose={() => setModal(null)}
              onSuccess={() => {
                setModal(null);
                loadUsers(1, false);
                setRosterRevision((value) => value + 1);
              }}
            />
          ) : modal.type === "import" ? (
            <MmtImportFlow
              session={session}
              customerType={getPersonType(userInfo)}
              personLabels={personLabels}
              isExternalIdTaken={importDuplicateCheck}
              onClose={() => setModal(null)}
              onImported={() => {
                loadUsers(1, false);
                setRosterRevision((value) => value + 1);
              }}
            />
          ) : modal.type === "view" ? (
            <MmtEditTestPerson
              readOnly
              seniorUuidProp={modal.seniorUuid}
              onClose={() => setModal(null)}
            />
          ) : null}
        </div>
      ) : null}
    </main>
  );
}

const MENU_PATHS = {
  stats: <path d="M18 20V10M12 20V4M6 20v-6" />,
  password: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </>
  ),
  privacy: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  terms: (
    <>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  support: (
    <path d="M21 11.5a8.5 8.5 0 01-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1121 11.5z" />
  ),
  logout: (
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  ),
};
function MenuEntry({ icon, title, description, onClick, featured }) {
  return (
    <button
      className={`mmt-menu-item${featured ? " mmt-menu-item-feature" : ""}`}
      onClick={onClick}
    >
      <span className="mmt-menu-item-icon">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#054997"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {MENU_PATHS[icon]}
        </svg>
      </span>
      <span className="mmt-menu-item-text">
        <strong>{title}</strong>
        <span>{description}</span>
      </span>
      <svg
        className="mmt-menu-chevron"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#94a3b8"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    </button>
  );
}
